import React from 'react';
import { Box, FloatingMenu, Forms, Help, Loading, StringHelpers, SystemText, Tabs } from '@salesperformanceint/e4shared';
import PeriodSelector from '../../../periodSelector';
import { useSelector } from 'react-redux';
import Behaviours from '../behaviours';
import Metrics from '../metrics';
import All from '../all';
import CompetencyTasks from 'views/user/components/competencyTasks';
import ManageTask from 'views/user/components/manageTask';
import MetricsBreakdown from '../../../crmMetrics/components/breakdown';
import MetricsOverTime from '../../../crmMetrics/components/overTime';
import IntegrationOverTime from '../../../integrationMetrics/components/overTime';
import { apiGet, apiPatch } from 'services';
import Filter from 'components/filter';
import Focus from '../focus';
import CompetencyOverTime from '../../../competencyOverTime';
import posthog from 'posthog-js';

export const PerformanceTabs = ({ text, profile }) => {
    const { user } = useSelector(state => state.user);
    const [isMe, setIsMe] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const [reloading, setReloading] = React.useState(false);
    const [range, setRange] = React.useState('month');
    const [offset, setOffset] = React.useState(0);
    const [benchmark, setBenchmark] = React.useState(0);
    const [tab, setTab] = React.useState('behavior');
    const [activeTab, setActiveTab] = React.useState('behavior');
    const [popup, setPopup] = React.useState('');
    const [entity, setEntity] = React.useState({});
    const [data, setData] = React.useState([]);
    const [pathways, setPathways] = React.useState(null);
    const [focus, setFocus] = React.useState(null);
    const [search, setSearch] = React.useState({});
    const [competencies, setCompetencies] = React.useState([]);

    const api = {
        flag: (raw, competency) => {
            const behaviour = { ...raw, profile_Id: profile.id, flagged: !raw.flagged };
            const url = competency ? 'flag' : 'performance/flag';
            apiPatch(url, behaviour, user.guid, () => {
                const temp = [...data];

                if (!competency) {
                    const thisBehaviour = temp.find(x => x.id === raw.id);
                    thisBehaviour.flagged = !thisBehaviour.flagged;
                    setData(temp);
                } else {
                    const newTemp = [];
                    temp.map(x => {
                        if (x.competency.id === raw.id) {
                            newTemp.push({ ...x, competency: { ...x.competency, flagged: !x.competency.flagged } });
                        } else {
                            newTemp.push(x);
                        }
                    });

                    setData(newTemp);
                }
            });
        },
    };

    const loadData = React.useCallback(
        reloading => {
            apiGet(
                `performance-dashboard/${profile.id}?reloading=${reloading}&range=${range}&offset=${offset}&benchmark=${benchmark}`,
                user.guid,
                response => {
                    setData(response.competencies);
                    if (!reloading) {
                        setPathways(response.pathways);
                        setFocus(response.whatsHappening);
                    }

                    setLoading(false);
                    setReloading(false);
                }
            );
        },
        [profile, benchmark, range, offset]
    );

    React.useEffect(() => {
        const temp = (data || [])
            ?.reduce((a, c) => {
                return a.some(x => x.id === c.id) ? a : a.concat([c]);
            }, [])
            .map(c => {
                c.crmJobs = data
                    .reduce((a, c) => a.concat(c.metrics.filter(m => m.type === 'CRM')), [])
                    .reduce((a, c) => (a.some(x => x.id === c.id) ? a : a.concat([c])), []);
                c.integrationFields = data
                    .reduce((a, c) => a.concat(c.metrics.filter(m => m.type === 'Integration')), [])
                    .reduce((a, c) => (a.some(x => x.id === c.id) ? a : a.concat([c])), []);

                return c;
            })
            .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));

        setCompetencies([...temp]);
    }, [data]);

    React.useEffect(() => {
        if (profile && !reloading) {
            setLoading(true);
            loadData();
        }
    }, [profile]);

    React.useEffect(() => {
        if (profile && !loading && !reloading) {
            setReloading(true);
            loadData(true);
        }
    }, [benchmark, range, offset]);

    React.useEffect(() => {
        setIsMe(profile?.id === user?.id);
    }, [profile, user]);

    React.useEffect(() => {
        if (tab !== activeTab) {
            posthog.capture('performance-dashboard-tab-changed', { tab });
            setActiveTab(tab);
        }
    }, [tab]);

    const show = (p, e) => {
        setPopup(p);
        setEntity(e);
    };

    const onFilter = filters => {
        const temp = { ...search };
        temp.filters = filters;
        setSearch(temp);
    };

    const onSearch = text => {
        const temp = { ...search };
        temp.text = text;
        setSearch(temp);
    };

    const filters = [
        {
            ref: 'competencies',
            label: text.tabs.filters.capabilities,
            data: competencies.sort((a, b) => (a.name > b.name ? 1 : -1)),
        },
        {
            ref: 'objectives',
            label: text.tabs.filters.objectives,
            data: [
                { id: 'U', name: text.tabs.filters.objectivesUpcoming },
                { id: 'O', name: text.tabs.filters.objectivesOverdue },
            ],
        },
        {
            ref: 'score',
            label: text.tabs.filters.scores,
            data: [
                { id: 1, name: '1' },
                { id: 2, name: '2' },
                { id: 3, name: '3' },
                { id: 4, name: '4' },
                { id: 5, name: '5' },
            ],
        },
        { ref: 'flagged', label: text.tabs.filters.flagged, boolean: true },
    ];

    const tabButtons = (
        <div className="">
            <span className={`${tab !== 'all' ? 'hidden' : ''}  tab-search flex flex-center`}>
                <span className="inline flex flex-center text-input-holder">
                    <input
                        type="text"
                        value={search.text}
                        onChange={e => {
                            onSearch(e.target.value);
                            posthog.capture('performance-dashboard-search-changed');
                        }}
                        className="form-control"
                        placeholder="Search"
                        maxLength={50}
                    />
                    <i className="fa fa-search"></i>
                </span>
                <Filter
                    filters={filters}
                    onFilter={x => {
                        onFilter(x);

                        const temp = { ...x };
                        temp.capabilities = temp.competencies?.map(
                            x => filters.find(x => x.ref === 'competencies')?.data?.find(d => d.id === x)?.name
                        );

                        delete temp.competencies;

                        posthog.capture('performance-dashboard-filter-changed', { filters: temp });
                    }}
                />
            </span>
            <span className={`flex flex-row flex-center ${tab === 'all' ? 'hidden' : ''}`}>
                <label>{text.tabs.benchmark}</label>
                <label className="mr10">
                    <Help id="benchmark">
                        <div>
                            {isMe
                                ? SystemText('User121/Benchmark/Help/You')
                                : SystemText('User121/Benchmark/Help/Them', profile?.full_Name)}
                        </div>
                    </Help>
                </label>
                <FloatingMenu
                    buttonText={
                        benchmark ? profile.roles.find(r => r.id === benchmark)?.name || 'Role' : text.tabs.benchmarkTeam
                    }
                    small
                    className="btn"
                >
                    <li
                        data-testid="benchmark-0"
                        key="benchmark-0"
                        onClick={() => {
                            setBenchmark(0);
                            posthog.capture('performance-dashboard-benchmark-changed');
                        }}
                        className="flex flex-center"
                    >
                        {SystemText('User121/Benchmark/Team')}
                    </li>
                    {profile?.roles?.map(r => (
                        <li
                            data-testid={`benchmark-${r.id}`}
                            key={`benchmark-${r.id}`}
                            onClick={() => {
                                setBenchmark(r.id);
                                posthog.capture('performance-dashboard-benchmark-changed');
                            }}
                            className="flex flex-center"
                        >
                            {SystemText('User121/Benchmark/Role')} - {r.name}
                        </li>
                    ))}
                </FloatingMenu>
                <label className="ml20 mr10">{text.tabs.filter}</label>
                <PeriodSelector
                    data-testid="periodselector-crm-metrics"
                    range={range}
                    setRange={x => {
                        setRange(x);
                        posthog.capture('performance-dashboard-filtermetrics-changed');
                    }}
                    offset={offset}
                    setOffset={x => {
                        setOffset(x);
                        posthog.capture('performance-dashboard-filtermetrics-changed');
                    }}
                    white
                />
            </span>
        </div>
    );

    const common = {
        data,
        text,
        profile,
        loading,
        reloading,
        show,
        onFlag: api.flag,
    };

    return (
        <div>
            {loading ? (
                <Loading />
            ) : (
                <>
                    <div className="focus-tiles-wrapper">
                        <Focus data={focus} text={text} profile={profile} tabsLoading={loading} />
                        <Tabs
                            style="clean"
                            tab={tab}
                            setTab={setTab}
                            tabs={['behavior', 'metric', 'all']}
                            tabNames={{ behavior: text.tabs.behaviour, metric: text.tabs.metric, all: text.tabs.all }}
                            buttons={<>{tabButtons}</>}
                        ></Tabs>
                    </div>
                    <div className="mt20 tab-content">
                        {tab === 'behavior' && <Behaviours {...common} pathways={pathways} />}
                        {tab === 'metric' && <Metrics {...common} />}
                        {tab === 'all' && <All {...common} competencies={competencies} search={search} />}
                    </div>
                </>
            )}
            {popup === 'metric-line' && entity.ref === 'CRM' ? (
                <MetricsOverTime
                    profile={profile}
                    job={popup === 'metric-line' ? entity : null}
                    range={range}
                    show={popup === 'metric-line'}
                    close={() => {
                        setPopup('');
                        setEntity({});
                    }}
                />
            ) : (
                <IntegrationOverTime
                    profile={profile}
                    metric={popup === 'metric-line' ? entity : null}
                    range={range}
                    show={popup === 'metric-line'}
                    close={() => {
                        setPopup('');
                        setEntity({});
                    }}
                />
            )}
            <MetricsBreakdown
                profile={profile}
                job={popup === 'metric-pie' ? entity : null}
                show={popup === 'metric-pie'}
                range={range}
                offset={offset}
                close={() => {
                    setPopup('');
                    setEntity({});
                }}
            />
            <CompetencyOverTime
                profile={profile}
                competency={entity}
                show={popup === 'competency-line'}
                text={text.behaviours.overtime}
                close={() => setPopup('')}
            />
            <ManageTask
                show={popup === 'competency-add'}
                close={() => setPopup('')}
                values={{ profile_Ids: [profile?.id] }}
                defaultCompetency={entity.id}
            />
            <CompetencyTasks
                summary
                profile={profile}
                competency={{ ...entity, tasks: entity.tasks }}
                show={popup === 'competency-tasks'}
                close={() => setPopup('')}
            />
        </div>
    );
};

export default PerformanceTabs;
